import { AxiosResponse } from "axios"
import { init, vueRouterInstrumentation } from "@sentry/vue"
import { BrowserTracing } from "@sentry/tracing"
import Router from "@/router"
import { getAxiosInstance, AxiosConfig } from "@/services"
import { useAppStore, useEnvStore } from "@/store"
import { isAppDev, isAppTest, isNodeProd } from "@/utils"
import eruda from "eruda"
import { App } from "vue"
import { get } from "lodash-es"
import { Toast } from "vant"

// 初始化 Axios 示例并修改默认配置
export function initAxios() {
	const axios = getAxiosInstance()
	const appStore = useAppStore()
	axios.interceptors.request.use((config: AxiosConfig) => {
		const { headers = {}, module: requestModule = "common", url } = config ?? {}
		const currentEnv = useEnvStore().current
		const envModule = requestModule && currentEnv.modules[requestModule]
		// 如果配置loading为true，显示请求加载的loading
		if (config?.data?.loading || config?.params?.loading) {
			Toast.loading({
				message: "加载中...",
				forbidClick: true,
				duration: 0,
				loadingType: "spinner"
			})
			delete config?.data?.loading
			delete config?.params?.loading
		}
		if (appStore.isLogin) {
			headers["authorization"] = appStore.userHeader
		}
		// 如果请求地址以 http 开头,默认不处理(和 axios 保持一致)
		if (url && url.startsWith("http")) return config
		// 因为开发环境下后端没有跨域处理
		if (isNodeProd && envModule) {
			config.baseURL = envModule.url || config.baseURL
		} else if (envModule) {
			config.url = `/${envModule.proxyPrefix}${url}`
		}
		return config
	})

	// @ts-ignore
	function onRejected(error: AxiosResponse<AxiosResponseData<any>>) {
		Toast.clear()
		// @ts-ignore
		let { message: responseMessage, data } =
			// @ts-ignore
			get(error, "response", error) || error
		const { code, message: businessMessage } = data || {}
		let messageText = businessMessage || responseMessage || "发生错误"
		if (code === 401) {
			const appStore = useAppStore()
			messageText = "登录超时,请重新登录"
			// @ts-ignore
			appStore.setUserInfo(null)
			if (Router.currentRoute.value.path !== "/login") {
				Router.replace("/login")
			}
		}
		// @ts-ignore
		Toast({ message: messageText })
	}
		
	axios.interceptors.response.use(
		// @ts-ignore
		(response: AxiosResponse<AxiosResponseData<any>>) => {
			Toast.clear()
			const { code } = response.data || {}
			if (code !== 0) {
				onRejected(response)
				return Promise.reject(response)
			}
			return response
		},
		onRejected
	)
}

// sentry 埋点
export function initSentry(app: App, router: typeof Router) {
	if (isAppDev) return
	init({
		app,
		// 正式环境需要替换为正式环境地址
		dsn: "https://9839560123f34004812d1c69b96ba2e2@qaa-sentry.tope365.com/14",
		integrations: [
			new BrowserTracing({
				tracingOrigins: ["localhost", /^\//],
				routingInstrumentation: vueRouterInstrumentation(router)
			})
		],
		tracesSampleRate: 1.0
	})
}

// 初始化 eruda
export function initEruda() {
	if (!isAppTest) return
	eruda.init()
}
